import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { useForm, useWatch, type FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/ui/page-container";
import { Wizard } from "@/components/ui/wizard";
import type { WizardStep } from "@/components/ui/wizard/types";
import {
  TabsRoot,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs/tabs-primitives";
import {
  PROTECTED_ROUTES,
  buildRoute,
  buildScheduleReturnRoute,
} from "@/utils/navigation";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";
import { useGymChangeHandler } from "@/hooks/use-gym-change-handler";
import { ConfirmDialog } from "@/components/ui/dialog/confirm-dialog";
import {
  EventCreationSchema,
  type EventCreationFormData,
  VacancyCreationSchema,
  type VacancyCreationFormData,
} from "@/schemas/event";
import {
  EventTypeStep,
  EventDetailsStep,
  ReviewStep,
} from "@/components/create-event/steps";
import { VacancyWizardDetailsStep } from "@/components/create-event/steps/vacancy-wizard-details-step";
import { VacancyWizardReviewStep } from "@/components/create-event/steps/vacancy-wizard-review-step";
import {
  EVENTABLE_TYPE,
  EVENTABLE_TYPES,
  GYM_SCHEDULE_VIEW_TYPES,
  type GymScheduleViewType,
} from "@/constants/events";
import { z } from "zod";
import { RECURRENCE } from "@/utils/enum/dates";
import { useCreateEvent, useCreateVacancy } from "@/queries/use-events";
import { useStaffMember } from "@/queries/use-staff-members";
import { useAthlete } from "@/queries/use-athletes";
import {
  useCreateEventBlueprint,
  useUpdateEventBlueprint,
  useEventBlueprint,
} from "@/queries/use-gym-schedule-blueprint";
import { useGymStore } from "@/stores/gym-store";
import { useGymScheduleStore } from "@/stores/gym-schedule-store";
import { useAuthStore } from "@/stores/auth-store";
import type { DateConstraints } from "@/types/date";
import { toast } from "@/components/ui/toast";
import { getApiErrorMessages } from "@/utils/api-error";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import type { CreateEventBlueprintParams } from "@/api/gym-schedule-blueprints/types";
import type { EventableType } from "@/api/events/types";
import { useEntityNameBreadcrumb } from "@/hooks/use-breadcrumb/use-breadcrumb-overrides";
import { getDay, parseISO } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import {
  formatWeekParam,
  getWeekdayDate,
  getMinSelectableDate,
} from "@/utils/format-date";

type NewEventTab = "session" | "vacancy";

const isGymScheduleViewType = (
  type: EventableType,
): type is GymScheduleViewType =>
  GYM_SCHEDULE_VIEW_TYPES.includes(type as GymScheduleViewType);

export const Route = createFileRoute("/(protected)/scheduling/events/new/")({
  loaderDeps: ({ search }: { search: { fixedType?: EventableType } }) => ({
    fixedType: search.fixedType,
  }),
  loader: ({ deps }) => ({
    hasActionButtons: true,
    breadcrumbKey: deps.fixedType
      ? `pages:calendar.addEventByType.${deps.fixedType}`
      : undefined,
  }),
  validateSearch: z.object({
    fixedType: z.enum(EVENTABLE_TYPES).optional(),
    blueprintId: z.coerce.number().optional(),
    eventBlueprintId: z.coerce.number().optional(),
    scheduleId: z.coerce.number().optional(),
    staffMemberId: z.coerce.number().optional(),
    athleteId: z.coerce.number().optional(),
    tab: z.enum(["session", "vacancy"]).optional(),
    day: z.string().optional(),
  }),
  beforeLoad: ({ search }) => {
    const { gymScheduleId } = useGymScheduleStore.getState();
    if (
      !gymScheduleId &&
      !search.blueprintId &&
      !search.staffMemberId &&
      !search.athleteId
    ) {
      throw redirect({ to: PROTECTED_ROUTES.CALENDAR });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    fixedType,
    blueprintId,
    eventBlueprintId,
    scheduleId,
    staffMemberId: contextStaffMemberId,
    athleteId: contextAthleteId,
    tab: initialTab,
    day: searchDay,
  } = Route.useSearch();
  const { selectedGym } = useGymStore();
  const { data: contextStaffMember } = useStaffMember(
    contextStaffMemberId ?? 0,
    { enabled: !!contextStaffMemberId },
  );
  const { data: contextAthlete } = useAthlete(contextAthleteId ?? 0, {
    enabled: !!contextAthleteId,
  });
  const currentStaffMemberId = useAuthStore((s) => s.getUserProfile()?.id);
  const { mutate: createEvent, isPending: isCreatingEvent } = useCreateEvent();
  const { mutate: createVacancy, isPending: isCreatingVacancy } =
    useCreateVacancy();
  const { mutate: createEventBlueprint, isPending: isCreatingBlueprint } =
    useCreateEventBlueprint();
  const { mutate: updateEventBlueprint, isPending: isUpdatingBlueprint } =
    useUpdateEventBlueprint();
  const {
    gymScheduleId,
    gymScheduleStartsAt,
    gymScheduleEndsAt,
    clearGymSchedule,
  } = useGymScheduleStore();

  const isBlueprint = !!blueprintId;
  const isEditingBlueprint = isBlueprint && !!eventBlueprintId;

  const { data: existingBlueprint } = useEventBlueprint(
    selectedGym?.id ?? 0,
    blueprintId ?? 0,
    eventBlueprintId ?? 0,
  );

  const dateConstraints = useMemo((): DateConstraints => {
    const timezone = selectedGym?.timezone;
    const today = getMinSelectableDate(timezone);
    if (gymScheduleStartsAt && gymScheduleEndsAt) {
      const scheduleStart = new Date(gymScheduleStartsAt);
      const scheduleEnd = new Date(gymScheduleEndsAt);
      const zonedScheduleStart = timezone
        ? toZonedTime(scheduleStart, timezone)
        : scheduleStart;
      const zonedScheduleEnd = timezone
        ? toZonedTime(scheduleEnd, timezone)
        : scheduleEnd;
      if (zonedScheduleEnd < today) return { minDate: today };
      return {
        minDate: zonedScheduleStart > today ? scheduleStart : today,
        maxDate: scheduleEnd,
      };
    }
    return { minDate: today };
  }, [gymScheduleStartsAt, gymScheduleEndsAt, selectedGym?.timezone]);

  const schema = EventCreationSchema();

  const defaultValues = useMemo((): EventCreationFormData => {
    if (isEditingBlueprint && existingBlueprint) {
      const day =
        searchDay ||
        (gymScheduleStartsAt
          ? getWeekdayDate(existingBlueprint.weekday, gymScheduleStartsAt)
          : "");

      return {
        title: existingBlueprint.title ?? "",
        eventableType:
          existingBlueprint.eventableType === "groupSession"
            ? EVENTABLE_TYPE.GroupSession
            : EVENTABLE_TYPE.FloorDuty,
        difficultyLevel: existingBlueprint.difficultyLevel ?? undefined,
        disciplineId: existingBlueprint.discipline?.id,
        roomId: existingBlueprint.gymRoom?.id,
        recommendedCapacity: existingBlueprint.recommendedCapacity ?? undefined,
        roomCapacity: existingBlueprint.gymRoom?.maximumCapacity ?? null,
        staffMemberId: existingBlueprint.staffMember?.id,
        staffMemberName: existingBlueprint.staffMember?.name,
        disciplineAbbreviation: existingBlueprint.discipline?.abbreviation,
        disciplineColor: existingBlueprint.discipline?.color,
        roomName: existingBlueprint.gymRoom?.name,
        day,
        description: existingBlueprint.description ?? "",
        startTime: existingBlueprint.startsAt,
        endTime: existingBlueprint.endsAt,
        recurrenceRules: { frequency: RECURRENCE.Never, until: "" },
        gymScheduleId: gymScheduleId ?? scheduleId ?? null,
        isAnchorEvent: false,
      };
    }

    return {
      title: "",
      eventableType:
        fixedType ||
        (contextStaffMemberId
          ? EVENTABLE_TYPE.PersonalTrainingSession
          : contextAthleteId
            ? EVENTABLE_TYPE.PhysicalEvaluationSession
            : EVENTABLE_TYPE.GroupSession),
      difficultyLevel: undefined,
      disciplineId: undefined,
      roomId: undefined,
      recommendedCapacity: undefined,
      roomCapacity: null,
      staffMemberId: contextStaffMemberId,
      staffMemberName: contextStaffMember
        ? `${contextStaffMember.firstName} ${contextStaffMember.lastName}`
        : undefined,
      athleteId: contextAthleteId,
      athleteName: contextAthlete
        ? `${contextAthlete.firstName} ${contextAthlete.lastName}`
        : undefined,
      day: "",
      description: "",
      startTime: "",
      endTime: "",
      recurrenceRules: { frequency: RECURRENCE.Never, until: "" },
      gymScheduleId: gymScheduleId ?? scheduleId ?? null,
      isAnchorEvent: false,
    };
  }, [
    isEditingBlueprint,
    existingBlueprint,
    fixedType,
    contextStaffMemberId,
    contextStaffMember,
    contextAthleteId,
    contextAthlete,
    gymScheduleId,
    gymScheduleStartsAt,
    scheduleId,
    searchDay,
  ]);

  const form = useForm<EventCreationFormData>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    defaultValues,
  });

  const lastBlueprintIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (
      isEditingBlueprint &&
      existingBlueprint &&
      lastBlueprintIdRef.current !== eventBlueprintId
    ) {
      form.reset(defaultValues);
      lastBlueprintIdRef.current = eventBlueprintId ?? null;
    }
  }, [
    isEditingBlueprint,
    existingBlueprint,
    defaultValues,
    form,
    eventBlueprintId,
  ]);

  useEffect(() => {
    if (contextStaffMember) {
      form.setValue(
        "staffMemberName",
        `${contextStaffMember.firstName} ${contextStaffMember.lastName}`,
      );
    }
  }, [contextStaffMember, form]);

  useEffect(() => {
    if (contextAthlete) {
      form.setValue(
        "athleteName",
        `${contextAthlete.firstName} ${contextAthlete.lastName}`,
      );
    }
  }, [contextAthlete, form]);

  const showTabs = !!contextStaffMemberId;

  const [activeTab, setActiveTab] = useState<NewEventTab>(
    initialTab ?? "session",
  );
  const [pendingTab, setPendingTab] = useState<NewEventTab | null>(null);

  const vacancySchema = VacancyCreationSchema();

  const vacancyForm = useForm<VacancyCreationFormData>({
    resolver: zodResolver(vacancySchema),
    mode: "onSubmit",
    defaultValues: {
      startsAt: "",
      endsAt: "",
      reason: "",
      recurrenceRules: { frequency: RECURRENCE.Never, until: "" },
    },
  });

  const handleTabChange = (nextTab: string) => {
    const next = nextTab as NewEventTab;
    if (next === activeTab) return;
    const isActiveFormDirty =
      activeTab === "session"
        ? form.formState.isDirty
        : vacancyForm.formState.isDirty;
    if (isActiveFormDirty) {
      setPendingTab(next);
    } else {
      setActiveTab(next);
    }
  };

  const confirmTabSwitch = () => {
    if (pendingTab) setActiveTab(pendingTab);
    setPendingTab(null);
  };

  const eventableType = useWatch({
    control: form.control,
    name: "eventableType",
  });

  const title = useWatch({ control: form.control, name: "title" });
  const crumbLabel = title?.trim()
    ? title.trim()
    : showTabs
      ? t(
          activeTab === "vacancy"
            ? "pages:calendar.eventDetail.newVacancy"
            : "pages:calendar.eventDetail.newEvent",
        )
      : null;
  useEntityNameBreadcrumb(crumbLabel);

  const step2Fields = useMemo((): FieldPath<EventCreationFormData>[] => {
    if (eventableType === EVENTABLE_TYPE.FloorDuty) {
      if (isBlueprint) return ["staffMemberId", "startTime", "endTime"];
      return [
        "staffMemberId",
        "startTime",
        "endTime",
        "recurrenceRules.frequency",
        "recurrenceRules.until",
      ];
    }
    if (
      eventableType === EVENTABLE_TYPE.PersonalTrainingSession ||
      eventableType === EVENTABLE_TYPE.PhysicalEvaluationSession
    ) {
      const fields: FieldPath<EventCreationFormData>[] = [
        "staffMemberId",
        "athleteId",
        "startTime",
        "endTime",
      ];
      if (eventableType === EVENTABLE_TYPE.PersonalTrainingSession) {
        fields.push("recurrenceRules.frequency", "recurrenceRules.until");
      }
      return fields;
    }
    return [
      "title",
      "startTime",
      "endTime",
      "difficultyLevel",
      "disciplineId",
      "roomId",
      "recommendedCapacity",
      "staffMemberId",
      "description",
      "recurrenceRules.frequency",
      "recurrenceRules.until",
      "isAnchorEvent",
    ];
  }, [eventableType, isBlueprint]);

  const steps: WizardStep<EventCreationFormData>[] = useMemo(
    () => [
      {
        id: "event-type",
        label: t("pages:calendar.wizard.steps.eventType"),
        component: (props) => (
          <EventTypeStep
            {...props}
            availableTypes={
              contextStaffMemberId
                ? [
                    EVENTABLE_TYPE.PersonalTrainingSession,
                    EVENTABLE_TYPE.PhysicalEvaluationSession,
                  ]
                : contextAthleteId
                  ? [EVENTABLE_TYPE.PhysicalEvaluationSession]
                  : [EVENTABLE_TYPE.GroupSession, EVENTABLE_TYPE.FloorDuty]
            }
            dateConstraints={dateConstraints}
            isBlueprint={isBlueprint}
            fixedType={fixedType}
          />
        ),
        fields: ["eventableType", "day"],
      },
      {
        id: "event-details",
        label: t("pages:calendar.wizard.steps.eventDetails"),
        component: (props) => (
          <EventDetailsStep
            {...props}
            isBlueprint={isBlueprint}
            blueprintId={blueprintId}
            excludeEventId={eventBlueprintId}
            lockAthlete={!!contextAthleteId}
            hideRecurrence={!!contextStaffMemberId || !!contextAthleteId}
          />
        ),
        fields: step2Fields,
      },
      {
        id: "confirmation",
        label: t("pages:calendar.wizard.steps.confirmation"),
        component: (props) => (
          <ReviewStep {...props} isBlueprint={isBlueprint} />
        ),
      },
    ],
    [
      t,
      dateConstraints,
      isBlueprint,
      blueprintId,
      eventBlueprintId,
      step2Fields,
      fixedType,
      contextStaffMemberId,
      contextAthleteId,
    ],
  );

  const vacancySteps: WizardStep<VacancyCreationFormData>[] = useMemo(
    () => [
      {
        id: "vacancy-details",
        label: t("pages:calendar.wizard.steps.vacancyDetails"),
        component: (props) => <VacancyWizardDetailsStep {...props} />,
        fields: [
          "startsAt",
          "endsAt",
          "reason",
          "recurrenceRules.frequency",
          "recurrenceRules.until",
        ],
      },
      {
        id: "vacancy-confirmation",
        label: t("pages:calendar.wizard.steps.confirmation"),
        component: (props) => <VacancyWizardReviewStep {...props} />,
      },
    ],
    [t],
  );

  const navigateToEditor = useMemo(
    () =>
      scheduleId
        ? buildRoute.gymScheduleBlueprintEdit(scheduleId)
        : PROTECTED_ROUTES.CALENDAR,
    [scheduleId],
  );

  const returnTab =
    fixedType === EVENTABLE_TYPE.FloorDuty
      ? EVENTABLE_TYPE.FloorDuty
      : EVENTABLE_TYPE.GroupSession;

  const navigateBack = useCallback(
    (day?: string) => {
      if (scheduleId) {
        navigate({ to: navigateToEditor, search: { tab: returnTab } });
      } else if (contextStaffMemberId) {
        navigate(buildScheduleReturnRoute(contextStaffMemberId, day));
      } else if (contextAthleteId && currentStaffMemberId) {
        navigate(buildScheduleReturnRoute(currentStaffMemberId, day));
      } else if (gymScheduleStartsAt) {
        navigate({
          to: PROTECTED_ROUTES.CALENDAR,
          search: {
            week: formatWeekParam(gymScheduleStartsAt, selectedGym?.timezone),
          },
        });
      } else {
        navigate({ to: PROTECTED_ROUTES.CALENDAR });
      }
    },
    [
      navigate,
      navigateToEditor,
      scheduleId,
      returnTab,
      gymScheduleStartsAt,
      selectedGym?.timezone,
      contextStaffMemberId,
      contextAthleteId,
      currentStaffMemberId,
    ],
  );

  const hasAnyUnsavedChanges =
    form.formState.isDirty || vacancyForm.formState.isDirty;

  const { handleCancelEdit, unsavedChangesDialogProps } = useUnsavedChanges({
    isEditing: true,
    isDirty: hasAnyUnsavedChanges,
    onLeave: navigateBack,
  });

  useGymChangeHandler({
    hasUnsavedChanges: hasAnyUnsavedChanges,
    handleCancelEdit,
    navigateOnChange: PROTECTED_ROUTES.CALENDAR,
  });

  const handleComplete = (data: EventCreationFormData) => {
    if (!selectedGym?.id) {
      toast.error(t("pages:calendar.toasters.create.error.title"), {
        description: t("pages:calendar.toasters.create.error.description"),
      });
      return;
    }

    if (data.eventableType === EVENTABLE_TYPE.FloorDuty && !data.title) {
      data.title = t("pages:calendar.eventableType.floorDuty");
    }

    if (
      (data.eventableType === EVENTABLE_TYPE.PersonalTrainingSession ||
        data.eventableType === EVENTABLE_TYPE.PhysicalEvaluationSession) &&
      !data.title
    ) {
      data.title = t(`pages:calendar.eventableType.${data.eventableType}`);
    }

    if (isBlueprint && blueprintId) {
      if (!isGymScheduleViewType(data.eventableType)) return;
      if (!data.staffMemberId) return;

      const weekday = getDay(
        parseISO(data.day),
      ) as CreateEventBlueprintParams["weekday"];
      const blueprintData: CreateEventBlueprintParams = {
        eventableType: data.eventableType,
        weekday,
        startsAt: data.startTime,
        endsAt: data.endTime,
        staffMemberId: data.staffMemberId,
        title: data.title || undefined,
        description: data.description || undefined,
        recommendedCapacity: data.recommendedCapacity ?? undefined,
        difficultyLevel: data.difficultyLevel,
        gymRoomId: data.roomId,
        disciplineId: data.disciplineId,
      };

      const toasterKey = data.eventableType;

      if (isEditingBlueprint && eventBlueprintId) {
        updateEventBlueprint(
          {
            gymId: selectedGym.id,
            blueprintId,
            eventBlueprintId,
            data: blueprintData,
          },
          {
            onSuccess: () => {
              toast.success(
                t(
                  `pages:scheduling.gymScheduleEditor.toaster.update.success.title.${toasterKey}`,
                ),
                {
                  description: t(
                    `pages:scheduling.gymScheduleEditor.toaster.update.success.description.${toasterKey}`,
                  ),
                },
              );
              clearGymSchedule();
              navigateBack();
            },
            onError: (error) => {
              toast.error(
                t(
                  `pages:scheduling.gymScheduleEditor.toaster.update.error.${toasterKey}`,
                ),
                {
                  description: getApiErrorMessages(error),
                },
              );
            },
          },
        );
        return;
      }

      createEventBlueprint(
        { gymId: selectedGym.id, blueprintId, data: blueprintData },
        {
          onSuccess: () => {
            toast.success(
              t(
                `pages:scheduling.gymScheduleEditor.toaster.create.success.title.${toasterKey}`,
              ),
              {
                description: t(
                  `pages:scheduling.gymScheduleEditor.toaster.create.success.description.${toasterKey}`,
                ),
              },
            );
            clearGymSchedule();
            navigateBack();
          },
          onError: (error) => {
            toast.error(
              t(
                `pages:scheduling.gymScheduleEditor.toaster.create.error.${toasterKey}`,
              ),
              {
                description: getApiErrorMessages(error),
              },
            );
          },
        },
      );
      return;
    }

    createEvent(
      { gymId: selectedGym.id, form: data, timezone: selectedGym.timezone },
      {
        onSuccess: () => {
          toast.success(t("pages:calendar.toasters.create.success.title"), {
            description: t(
              "pages:calendar.toasters.create.success.description",
            ),
          });
          clearGymSchedule();
          navigateBack(data.day);
        },
        onError: (error) => {
          toast.error(t("pages:calendar.toasters.create.error.title"), {
            description: getApiErrorMessages(error),
          });
        },
      },
    );
  };

  const handleVacancyComplete = (data: VacancyCreationFormData) => {
    if (!selectedGym?.id) {
      toast.error(t("pages:calendar.toasters.create.error.title"), {
        description: t("pages:calendar.toasters.create.error.description"),
      });
      return;
    }

    createVacancy(
      { gymId: selectedGym.id, form: data },
      {
        onSuccess: () => {
          toast.success(
            t(
              "components:createEventModal.tabs.vacancy.toaster.create.success.title",
            ),
            {
              description: t(
                "components:createEventModal.tabs.vacancy.toaster.create.success.description",
              ),
            },
          );
          clearGymSchedule();
          navigateBack(formatWeekParam(data.startsAt, selectedGym?.timezone));
        },
        onError: (error) => {
          toast.error(
            t(
              "components:createEventModal.tabs.vacancy.toaster.create.error.title",
            ),
            {
              description: getApiErrorMessages(error),
            },
          );
        },
      },
    );
  };

  const handleCancel = () => {
    handleCancelEdit(() => {
      clearGymSchedule();
      navigateBack();
    });
  };

  const isSubmitting =
    isCreatingEvent ||
    isCreatingBlueprint ||
    isUpdatingBlueprint ||
    isCreatingVacancy;

  const pageTitle = showTabs
    ? t(
        activeTab === "vacancy"
          ? "pages:calendar.eventDetail.newVacancy"
          : "pages:calendar.eventDetail.newEvent",
      )
    : t("pages:calendar.eventDetail.newEvent");

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <MobilePageTitle>{pageTitle}</MobilePageTitle>
      <PageContainer
        className="flex min-h-0 min-w-[309px] flex-1 flex-col p-0 md:m-0 lg:mb-0"
        internalScroll
      >
        {showTabs ? (
          <TabsRoot
            value={activeTab}
            onValueChange={handleTabChange}
            className="h-full min-h-0"
          >
            <TabsList>
              <TabsTrigger value="session" className="py-3">
                {t("components:createEventModal.tabs.session.title")}
              </TabsTrigger>
              <TabsTrigger value="vacancy" className="py-3">
                {t("components:createEventModal.tabs.vacancy.title")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="session" className="h-full min-h-0">
              <Wizard
                form={form}
                steps={steps}
                onComplete={handleComplete}
                isSubmitting={isSubmitting}
                onCancel={handleCancel}
              />
            </TabsContent>
            <TabsContent value="vacancy" className="h-full min-h-0">
              <Wizard
                form={vacancyForm}
                steps={vacancySteps}
                onComplete={handleVacancyComplete}
                isSubmitting={isSubmitting}
                onCancel={handleCancel}
              />
            </TabsContent>
          </TabsRoot>
        ) : (
          <Wizard
            form={form}
            steps={steps}
            onComplete={handleComplete}
            isSubmitting={isSubmitting}
            onCancel={handleCancel}
          />
        )}
      </PageContainer>
      <ConfirmDialog {...unsavedChangesDialogProps} />
      {showTabs && (
        <ConfirmDialog
          isOpen={pendingTab !== null}
          onOpenChange={(open) => {
            if (!open) setPendingTab(null);
          }}
          onConfirm={confirmTabSwitch}
          title={t("common:unsavedChangesDialog.title")}
          description={t("common:unsavedChangesDialog.description")}
        />
      )}
    </div>
  );
}
